'use client';

import type { Configuration, Middleware } from '@repo/api-gw-sdk';
import { useSearchParams } from 'next/navigation';
import { createContext, useMemo, type PropsWithChildren } from 'react';

import { nonProjectDalFactory } from '@/data/dal/dalFactory';
import {
  createHttpClient,
  refreshTokenIfNeeded,
  type HttpClient,
} from '@/data/dal/httpClient';

import { createConfig } from './api-config';
import type { Environment } from './environment';
import { useEnvironment } from './useEnvironment';

const createMiddleware = (
  authToken: string,
  updateAuthToken: (token: string) => void,
  environment: Environment,
  serverFlags: string | null
): Middleware => {
  const useRefreshToken = !(
    environment.isDemo ||
    environment.isDev ||
    environment.isTest
  );

  let refreshPromise = Promise.resolve(authToken);
  let isRefreshing = false;

  const refreshToken = () => {
    if (!useRefreshToken || isRefreshing) {
      return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = refreshTokenIfNeeded(
      authToken,
      environment.clientApiBaseUrl,
      updateAuthToken
    ).then(
      (token) => {
        isRefreshing = false;
        return token;
      },
      () => {
        isRefreshing = false;
        return authToken;
      }
    );
    return refreshPromise;
  };

  return {
    pre: async (context) => {
      if (serverFlags) {
        context.setHeaderParam('X-Server-Flags', serverFlags);
      }

      if (!context.getUrl().endsWith('/refresh')) {
        const newAuthToken = await refreshToken();
        context.setHeaderParam('Authorization', `Bearer ${newAuthToken}`);
      }

      return context;
    },
    post: async (context) => {
      if (useRefreshToken && context.httpStatusCode === 401) {
        // The user is not authenticated, delete the next jwt and redirect to login
        location.href = '/logout';
      }
      return context;
    },
  };
};

export const NonProjectDalContext = createContext<{
  nonProjectDal: ReturnType<typeof nonProjectDalFactory>;
  httpClient: HttpClient;
  configuration: Configuration;
} | null>(null);

export const NonProjectDalProvider = ({ children }: PropsWithChildren) => {
  const env = useEnvironment();
  const searchParams = useSearchParams();
  const serverFlags = searchParams.get('serverFlags');

  const configuration = useMemo(() => {
    const config = createConfig(env.clientApiBaseUrl, env.authToken);
    config.middleware!.push(
      createMiddleware(env.authToken, env.updateAuthToken, env, serverFlags)
    );
    return config;
  }, [env, serverFlags]);

  const httpClient = useMemo(
    () => createHttpClient(env.authToken, env.updateAuthToken, env),
    [env]
  );

  const nonProjectDal = useMemo(
    () =>
      nonProjectDalFactory(httpClient, configuration, env.isDev || env.isTest),
    [configuration, httpClient, env]
  );

  return (
    <NonProjectDalContext.Provider
      value={{ nonProjectDal, httpClient, configuration }}
    >
      {children}
    </NonProjectDalContext.Provider>
  );
};
